import { useCallback, useMemo } from 'react';
import {
  fetchAccountUsersSearch,
  type AccountUser,
} from '@/api/useAccountUsers';
import type { SelectOption } from '@kula-ai/components';

interface UseAccountUsersFieldConfigProps {
  name: string;
  label: string;
  isMulti?: boolean;
  condition?: Record<string, unknown>;
  customOptionMapper?: (_user: AccountUser) => SelectOption;
  placeholder?: string;
  initialQuery?: string;
  config?: Record<string, unknown>;
  fieldProps?: Record<string, unknown>;
}

export const useAccountUsersFieldConfig = ({
  name,
  label,
  isMulti,
  condition,
  config,
  customOptionMapper,
  initialQuery,
  placeholder,
  fieldProps,
}: UseAccountUsersFieldConfigProps) => {
  const loadOptions = useCallback(
    async (query: string) => {
      const { users } = await fetchAccountUsersSearch(query);
      if (customOptionMapper) return users.map(customOptionMapper);
      return users.map((user) => ({
        value: String(user.id),
        label: `${user.first_name} ${user.last_name}`,
        description: user.email,
        image: user.avatar_thumb_url,
      }));
    },
    [customOptionMapper]
  );

  const fieldConfig = useMemo(() => {
    // `defaultOptions` is overloaded by react-select: an array means
    // "preloaded; don't fetch on open", boolean `true` means "auto-fetch
    // on first menu open with empty query". Callers historically pass
    // an array of pre-selected users expecting them to render as chips
    // — but that suppresses the auto-fetch, leaving the dropdown empty
    // save for the already-selected items (the `useInterviewerField`
    // wizard hook used to work around this).
    //
    // Split the two roles here so every caller benefits:
    //   - the incoming array → top-level `options` (for the value→chip
    //     resolver to find pre-filled values before loadOptions runs);
    //   - `fieldProps.defaultOptions` is forced to `true` so the menu
    //     always auto-fetches on first open.
    const incoming = (fieldProps as Record<string, unknown> | undefined) ?? {};
    const preloadedOptions = Array.isArray(incoming.defaultOptions)
      ? (incoming.defaultOptions as Array<Record<string, unknown>>)
      : undefined;
    const { defaultOptions: _drop, ...restFieldProps } = incoming;

    const fp: Record<string, unknown> = {
      loadOptions,
      // No `'a'` default — react-select auto-loads with an empty query
      // when `defaultOptions: true`. Callers can still override.
      initialQuery,
      placeholder,
      ...restFieldProps,
      defaultOptions: true,
    };
    return {
      type: 'asyncSelect' as const,
      name,
      label,
      isMulti: isMulti ?? true,
      condition,
      ...(preloadedOptions ? { options: preloadedOptions } : {}),
      ...(config ?? {}),
      fieldProps: fp,
    };
  }, [
    condition,
    config,
    fieldProps,
    initialQuery,
    isMulti,
    label,
    loadOptions,
    name,
    placeholder,
  ]);

  return { fieldConfig };
};
